import { ResolverTag } from '@ps/web-core-enums';
import { Translation } from '@ps/web-shared-components';
import React from 'react';
import { useSelector } from 'react-redux';

import {
  ExclusionResponse,
  ExclusionTitle,
  TakeABreakFromUsInformation,
  useSelfExclusionData
} from '@swf/account';
import AccountVerification from '@swf/components/AccountVerification/AccountVerification';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import useWizardExit from '@swf/core/hooks/useWizardExit';
import { select } from '@swf/core/store';
import { AMLSuspended } from '@swf/notification';

import type { HelpSection } from '../../../../types/Cashier';
import Container from '../../../Container/Container';
import SuspendRM from '../../../Withdraw/components/SuspendRM/SuspendRM';
import HelpSectionContent from '../../../Withdraw/components/WithdrawRestriction/components/HelpSectionContent/HelpSectionContent';

import { TXTCLI_Verify_Account, TXTWCL_Help_with_deposits } from './DepositSuspended.messages.json';
import { page } from './DepositSuspended.scss';

const DepositSuspended: React.FC = () => {
  const exitWizard = useWizardExit();
  const userId = useSelector(select.account.getUserId);
  const amlSuspended = useSelector(select.account.amlSuspended);
  const isSelfExcluded = useSelector(select.account.getSelfExclusion);
  const nationalExclusion = useSelector(select.account.bannedByRegulator);
  const isSuspendedRM = useSelector(select.account.isSuspendRM);
  const currentlyTakingBreak = isSelfExcluded || nationalExclusion;
  const { selfExclusionData } = useSelfExclusionData();
  const { timeUntil, daysRequested } = selfExclusionData || {};
  const {
    data: { cmsHeaderAndGridLayout }
  } = useWizardContext();

  const renderContent = () => {
    switch (true) {
      case isSuspendedRM:
        return <SuspendRM />;
      case currentlyTakingBreak:
        return (
          <div>
            <ExclusionResponse timeUntil={timeUntil} daysRequested={daysRequested} />
            <TakeABreakFromUsInformation />
          </div>
        );
      default:
        return (
          <AccountVerification onClose={exitWizard} onComplete={exitWizard} showButton>
            {amlSuspended && <AMLSuspended userId={userId || ''} suspendedOnly />}
          </AccountVerification>
        );
    }
  };

  const helpSection: HelpSection = {
    title: TXTWCL_Help_with_deposits,
    body: <HelpSectionContent />
  };

  return (
    <Container
      page={DepositSuspended}
      headerProps={{
        title: currentlyTakingBreak ? (
          <ExclusionTitle daysRequested={daysRequested} />
        ) : (
          <Translation {...TXTCLI_Verify_Account} />
        ),
        buttons: { back: true, exitWizardOnBackClick: true }
      }}
      formClassName={page}
      pageClassName={page}
      helpSection={helpSection}
      helpSupportResolver={ResolverTag.HelpRestrictedAccount}
      showFooter
      {...(cmsHeaderAndGridLayout &&
        currentlyTakingBreak && { pageTopTitle: <ExclusionTitle daysRequested={daysRequested} /> })}
    >
      {renderContent()}
    </Container>
  );
};

DepositSuspended.displayName = 'DepositSuspended';

export default DepositSuspended;
